import React, { ChangeEvent } from 'react'
import { CallToAction, Form, Row } from '@scentregroup/ui'
import AnalyticsDataWrapper from '@scentregroup/shared/components/analytics-data-wrapper'
import ConditionalIssueText, {
  IIssueText,
} from '@scentregroup/shared/components/conditional-issue-text'
import { IMarketplaceProductPromotion } from '@scentregroup/shared/types'
import { IFormChangeEvent, IFormValues, IFormErrors } from '../../form/use-form'
import { Input, OptionType } from '../../types/product-form-inputs'
import { IMKPProductValues, ProductFields } from '../../types/product-form'
import { IMKPProductErrors } from '../../form/product-validation'
import { IMarketplaceProductShop } from '../../types/marketplace-product'
import InputModules from '../input-modules'
import Quantity from '../quantity'
import style from './index.module.scss'

export interface IAnalyticsData {
  id: string
  algoliaObjectID?: string
  algoliaQueryID?: string
  algoliaPosition?: string
  algoliaIndexName?: string
  uniqueProductId?: string
  title: string
  shop: IMarketplaceProductShop
  variants?: string
  variantSku?: string
  price: string
  category?: string | null
  brand: string
  variantGroupCode: string
  applicablePromotions?: IMarketplaceProductPromotion[]
}

interface IProductForm {
  values: IFormValues<IMKPProductValues>
  analyticsData: IAnalyticsData
  errors: IFormErrors<IMKPProductErrors>
  handleChange: (e: IFormChangeEvent) => void
  handleBlur: (e: IFormChangeEvent) => void
  handleSubmit: (e: ChangeEvent<HTMLButtonElement>) => void
  clearValues: (e: string[]) => void
  options: Input[]
  modifiers: Input[]
  maxQuantity?: number
  disabled?: boolean
  stockError: IIssueText | null
  allowButtonSelect?: boolean
}

const ProductForm: React.FunctionComponent<
  React.PropsWithChildren<IProductForm>
> = ({
  handleBlur,
  handleChange,
  clearValues,
  values,
  errors,
  handleSubmit,
  options,
  modifiers,
  disabled = false,
  maxQuantity = 0,
  analyticsData,
  stockError,
  allowButtonSelect,
}) => {
  const merchandisedPromotion = analyticsData.applicablePromotions?.[0]

  const analytics = {
    'data-ecomm-atc': 'product',
    'data-ecomm-brand': analyticsData.brand,
    'data-product-retailer': analyticsData.shop.name,
    'data-product-retailer-id': analyticsData.shop.retailerId ?? '',
    'data-product-category': analyticsData.category ?? '',
    'data-product-id': analyticsData.variantGroupCode,
    'data-product-unique-id': analyticsData.uniqueProductId ?? '',
    'data-product-name': analyticsData.title,
    'data-product-price': analyticsData.price,
    'data-product-currency': 'AUD',
    'data-product-quantity': values.quantity ?? 1,
    'data-product-variant': analyticsData.variants,
    'data-product-variant-sku': analyticsData.variantSku,
    ...(merchandisedPromotion && {
      'data-product-auto-promotion-id': merchandisedPromotion.campaignId,
      'data-product-auto-promotion-name': merchandisedPromotion.title,
    }),
  }
  return (
    <Form
      method="POST"
      action="#"
      noValidate
      className={style.form}
      onSubmit={handleSubmit}
    >
      <InputModules
        disabled={disabled}
        handleBlur={handleBlur}
        handleChange={handleChange}
        values={values}
        errors={errors}
        inputs={options}
        type={OptionType.OPTION}
        clearValues={clearValues}
        allowButtonSelect={allowButtonSelect}
      />
      <InputModules
        disabled={disabled}
        handleBlur={handleBlur}
        handleChange={handleChange}
        values={values}
        errors={errors}
        inputs={modifiers}
        type={OptionType.MODIFIER}
        clearValues={clearValues}
      />
      <Quantity
        disabled={disabled}
        value={values.quantity ?? 1}
        max={maxQuantity}
        onValueChange={value =>
          handleBlur({ value, name: ProductFields.QUANTITY })
        }
      />
      <ConditionalIssueText issue={stockError} />
      <AnalyticsDataWrapper {...analytics} className={style.addToCartAnalytics}>
        <Row>
          <CallToAction
            data-test-id={'add-to-cart-button'}
            data-insights-object-id={analyticsData.algoliaObjectID}
            data-insights-query-id={analyticsData.algoliaQueryID}
            data-insights-index={analyticsData.algoliaIndexName}
            data-insights-position={analyticsData.algoliaPosition}
            data-product-auto-promotion-id={
              merchandisedPromotion && merchandisedPromotion.campaignId
            }
            data-product-auto-promotion-name={
              merchandisedPromotion && merchandisedPromotion.title
            }
            aria-label={'Add to cart'}
            className={style.addToCartButton}
            type="submit"
            disabled={disabled}
          >
            Add to cart
          </CallToAction>
        </Row>
      </AnalyticsDataWrapper>
    </Form>
  )
}

export default ProductForm
